// Frontend mirror of the backend scope-lifecycle freeze.
//
// The server is the authority: writes to a game's content are rejected with
// 409 GP-SCOPE-NOT-ACTIVE when the owning org / game / client isn't ACTIVE
// (see ScopeLifecycleFilter). This module mirrors that rule in the UI so we
// disable the relevant actions up front (with an explanatory tooltip) instead
// of letting the user click through to an error.
//
// A game's content (edit details, add client/build, live-ops writes) is
// editable only while ACTIVE. SUSPENDED is recoverable (reactivate); SUNSET is
// terminal/read-only. Lifecycle transitions (activate/suspend/sunset) are NOT
// gated by this — they're how you change state.

export const GAME_STATE = Object.freeze({
    ACTIVE: 'ACTIVE',
    SUSPENDED: 'SUSPENDED',
    SUNSET: 'SUNSET',
});

// The global "selected game" lifecycle indicators (header status badge + page banner) only make
// sense on pages scoped to ONE selected game. Suppress them on org/entity-management surfaces
// (most of /admin-settings lists many games and shows per-row status) and on account / system pages
// that carry no single-game scope — otherwise a suspended selection makes the whole multi-game
// management page read as "this game is suspended".
//
// NOTE: /ai-credits is intentionally NOT here — usage is per-game-client and the page is gated by
// RequireActiveScope (shows the "scope suspended" card), so the header badge should match the other
// game-context pages.
const NON_GAME_SCOPED_PREFIXES = [
    '/admin-settings', '/profile', '/releases',
    '/help', '/notifications',
];

// Carve-outs: two surfaces under /admin-settings ARE scoped to a single selected game, so they DO
// want the status indicators even though the blanket /admin-settings suppression above would hide
// them:
//   • Game User Management (/admin-settings/game-mgmt/game-access-mgmt) — scoped to the selected game.
//   • Game Workspace (/admin-settings/game-mgmt/games/:gameClientId/...) — scoped to the selected build.
// The bare games list (/admin-settings/game-mgmt/games) is Game Registration and /workspace is the
// game-grid picker — both are multi-game, so they intentionally do NOT match here and stay suppressed.
const GAME_SCOPED_GAME_SETTINGS_MATCHERS = [
    (p) => p === '/admin-settings/game-mgmt/game-access-mgmt'
        || p.startsWith('/admin-settings/game-mgmt/game-access-mgmt/'),
    (p) => /^\/admin-settings\/game-mgmt\/games\/[^/]+(?:\/.*)?$/.test(p),
];

export function isSelectedGameStatusRelevant(pathname) {
    if (!pathname) return false;
    const underNonScoped = NON_GAME_SCOPED_PREFIXES.some(
        (pre) => pathname === pre || pathname.startsWith(pre + '/'),
    );
    if (!underNonScoped) return true;
    // Only /admin-settings has single-game-scoped pages under it; the carve-out matchers re-admit them.
    return GAME_SCOPED_GAME_SETTINGS_MATCHERS.some((m) => m(pathname));
}

/**
 * Effective lifecycle state of a game CLIENT for display, honoring containment: a client under a
 * suspended/sunset game is effectively frozen regardless of its own stored state (the backend guard
 * enforces this by walking org → game → client). Sunset cascades as terminal; suspended overrides
 * an active client; otherwise the client's own state stands.
 */
export function effectiveClientState(gameState, clientState) {
    if (gameState === GAME_STATE.SUNSET) return GAME_STATE.SUNSET;
    if (gameState === GAME_STATE.SUSPENDED && clientState === GAME_STATE.ACTIVE) return GAME_STATE.SUSPENDED;
    return clientState || gameState || null;
}

export function isGameActive(state) {
    return state === GAME_STATE.ACTIVE;
}

/** Human-readable reason a game's content is frozen, or null when it's editable. */
export function gameFrozenReason(state) {
    if (state === GAME_STATE.SUSPENDED) return 'Game is suspended — reactivate it to make changes.';
    if (state === GAME_STATE.SUNSET) return 'Game is sunset — it is read-only.';
    return null;
}

/** Human-readable reason a game CLIENT's content is frozen, or null when it's editable. */
export function clientFrozenReason(state) {
    if (state === GAME_STATE.SUSPENDED) return 'Client is suspended — reactivate it to make changes.';
    if (state === GAME_STATE.SUNSET) return 'Client is sunset — it is read-only.';
    return null;
}

/**
 * Action policy for a game CLIENT, honoring containment (a client under a non-active game is frozen
 * too — see {@link effectiveClientState}). An unknown/absent state is treated as editable so a
 * not-yet-saved build on the registration form isn't frozen.
 *
 * @returns {{active: boolean, canEditContent: boolean, frozenReason: (string|null)}}
 */
export function clientActionPolicy(gameState, clientState) {
    const frozenReason = clientFrozenReason(effectiveClientState(gameState, clientState));
    return {
        active: !frozenReason,
        // Edit the client's name, regions, platform mappings, operational settings, and secrets.
        canEditContent: !frozenReason,
        frozenReason,
    };
}

/**
 * Action policy for a game in the given lifecycle state.
 * @returns {{active: boolean, canEditContent: boolean, frozenReason: (string|null)}}
 */
export function gameActionPolicy(state) {
    // Frozen means SUSPENDED/SUNSET only — a DRAFT game is still being set up by its wizard, and
    // the backend's own mutation guard accepts DRAFT alongside ACTIVE.
    const frozenReason = gameFrozenReason(state);
    return {
        active: !frozenReason,
        // Edit game details, add client/build, and any live-ops write.
        canEditContent: !frozenReason,
        frozenReason,
    };
}

/**
 * The server refusing to READ a frozen game's content (ScopeLifecycleFilter: 403 GP-SCOPE-NOT-ACTIVE) - as opposed
 * to the 409 a write gets. Decided 9 October 2026: a suspended or sunset game is no access, not read-only.
 */
export function isClosedScopeRead(error) {
    return error?.response?.status === 403 && error.response.data?.code === 'GP-SCOPE-NOT-ACTIVE';
}

const CLOSED_READ_GAP_MS = 5000;
let lastClosedRead = 0;

/** Tells the lifecycle hooks to re-resolve after a refused read - once per burst of refusals. */
export function notifyClosedScopeRead(now = Date.now()) {
    if (now - lastClosedRead < CLOSED_READ_GAP_MS) return false;
    lastClosedRead = now;
    if (typeof window !== 'undefined') window.dispatchEvent(new Event('gp-lifecycle-changed'));
    return true;
}
